import { type SignUpInput, signUpSchema } from '@amore/shared'
import { Alert } from '@amore/ui/components/alert'
import { AuthLayout } from '@amore/ui/components/auth-layout'
import { BrandWordmark } from '@amore/ui/components/brand'
import { Button } from '@amore/ui/components/button'
import { Field } from '@amore/ui/components/field'
import { Input } from '@amore/ui/components/input'
import { zodResolver } from '@hookform/resolvers/zod'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Divider, GoogleMark } from '@/features/auth/components/auth-ui'
import { signIn, signUp } from '@/lib/auth'
import { authErrorMessage } from '@/lib/error-messages'
import { ensureSession, refreshSession } from '@/lib/session'
import { validationMessage } from '@/lib/validation-message'
import { ensureFirstWorkspaceSlug } from '@/lib/workspace'

const searchSchema = z.object({
  redirect: z.string().optional(),
  email: z.string().optional(),
})

export const Route = createFileRoute('/signup')({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context, search }) => {
    const session = await ensureSession(context.queryClient)

    if (!session) {
      return
    }

    if (search.redirect && search.redirect !== '/workspaces') {
      throw redirect({ to: search.redirect })
    }

    throw redirect(await defaultRedirect(context.queryClient))
  },
  component: SignupPage,
})

async function defaultRedirect(queryClient: QueryClient) {
  const workspaceSlug = await ensureFirstWorkspaceSlug(queryClient)
  return { to: '/w/$workspaceSlug', params: { workspaceSlug } } as const
}

function SignupPage() {
  const { redirect: redirectTo, email } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [googlePending, setGooglePending] = useState(false)
  const invitedEmail = email?.trim() ?? ''
  const form = useForm<SignUpInput>({
    resolver: zodResolver(signUpSchema, { error: validationMessage }),
    defaultValues: { name: '', email: invitedEmail, password: '' },
  })

  async function finishAuthentication() {
    await refreshSession(queryClient)

    if (redirectTo && redirectTo !== '/workspaces') {
      await navigate({ to: redirectTo })
      return
    }

    const workspaceSlug = await ensureFirstWorkspaceSlug(queryClient)
    await navigate({ to: '/w/$workspaceSlug', params: { workspaceSlug } })
  }

  const submit = form.handleSubmit(async (values) => {
    setFormError(null)
    const { error } = await signUp.email(values)

    if (error) {
      setFormError(authErrorMessage(error, 'Účet se nepodařilo vytvořit.'))
      return
    }

    await finishAuthentication()
  })

  async function signUpWithGoogle() {
    setGooglePending(true)
    setFormError(null)

    try {
      const callbackURL = redirectTo && redirectTo !== '/workspaces' ? redirectTo : '/'
      const errorSearch = new URLSearchParams()

      if (redirectTo) {
        errorSearch.set('redirect', redirectTo)
      }
      if (invitedEmail) {
        errorSearch.set('email', invitedEmail)
      }

      const { error } = await signIn.social({
        provider: 'google',
        callbackURL,
        errorCallbackURL: `/signup${errorSearch.size ? `?${errorSearch}` : ''}`,
      })

      if (error) {
        setFormError(authErrorMessage(error, 'Registraci přes Google se nepodařilo spustit.'))
      }
    } finally {
      setGooglePending(false)
    }
  }

  return (
    <AuthLayout brand={<BrandWordmark />}>
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-action">Začněme spolu</p>
        <h1 className="mt-3 font-heading text-4xl tracking-[-0.035em] text-foreground">
          Vytvořit účet
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-muted-foreground">
          Založte si jedno klidné místo pro všechny části vašeho svatebního dne.
        </p>
      </div>

      <Button
        type="button"
        appearance="outline"
        tone="neutral"
        className="mt-7 w-full"
        pending={googlePending}
        onClick={() => void signUpWithGoogle()}
      >
        <GoogleMark />
        {googlePending ? 'Přesměrovávám…' : 'Registrovat přes Google'}
      </Button>

      <Divider />

      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field controlId="name" label="Jméno" error={form.formState.errors.name?.message}>
          <Input autoComplete="name" placeholder="Vaše jméno" {...form.register('name')} />
        </Field>

        <Field controlId="email" label="E-mail" error={form.formState.errors.email?.message}>
          <Input
            type="email"
            autoComplete="email"
            readOnly={Boolean(invitedEmail)}
            placeholder="vy@example.com"
            {...form.register('email')}
          />
        </Field>

        <Field controlId="password" label="Heslo" error={form.formState.errors.password?.message}>
          <Input
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            {...form.register('password')}
          />
        </Field>

        {formError ? <Alert>{formError}</Alert> : null}

        <Button
          type="submit"
          appearance="solid"
          className="mt-1 w-full"
          pending={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? 'Vytvářím účet…' : 'Vytvořit účet'}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Už účet máte?{' '}
        <Link
          to="/login"
          search={{ ...(redirectTo ? { redirect: redirectTo } : {}), ...(email ? { email } : {}) }}
          className="font-semibold text-action hover:text-accent-foreground"
        >
          Přihlásit se
        </Link>
      </p>
    </AuthLayout>
  )
}
